// src/table/AppMovReactTable.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import styled from "styled-components";
import { useFilters, useSortBy, usePagination } from "react-table";

import { getMovs, deleteMov } from "../helpers/rutaMovs";
import { getHoms } from "../helpers/rutaHoms";
import { getClienteByCodigo } from "../helpers/rutaClientes";

import ModalFormMov from "../components/ModalFormMov";
import Table from "./TableContainer";
import "../css/tablecomandas.css";

/* ---------------- styles ---------------- */
const Styles = styled.div`
  .table-container { overflow: auto; height: 600px; }
  table { border: 1px solid black; border-spacing: 0; font-size: 13px; }
  th, td { padding: 0.5rem; border-bottom: 1px solid black; border-right: 1px solid black; }
  th { background-color: #778899; position: sticky; top: 0; z-index: 2; text-align: center; font-size: 12px; }
  td { background-color: white; }
  .pagination { padding: 0.5rem; background-color: #778899; font-size: 15px; font-weight: bold; }
`;

/* ---------------- utils ---------------- */
function toStartOfDay(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  return isNaN(d.getTime()) ? null : d;
}
function toEndOfDay(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T23:59:59");
  return isNaN(d.getTime()) ? null : d;
}
function norm(v) {
  return (v ?? "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}
const fmtDateAR = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
};
const toISO = (dt) =>
  `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;

/* =======================================================
   Componente
======================================================= */
export default function AppMovReactTable() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [movEdit, setMovEdit] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Catálogos para mostrar nombres (sin romper migración por código)
  const [homMap, setHomMap] = useState({});       // codhom -> hom
  const [cliMap, setCliMap] = useState({});       // codcli -> razonsocial

  // ---- filtros controlados ----
  const [filtros, setFiltros] = useState({
    desde: "",
    hasta: "",
    cliente: "",
    hom: "",
    usuario: "",
  });
  const setF = (k, v) => setFiltros((prev) => ({ ...prev, [k]: v }));

  /* ---------- cargar homs (map) ---------- */
  const loadHoms = useCallback(async () => {
    try {
      const list = await getHoms();
      const map = {};
      (list || [])
        .filter((h) => h?.activo !== false)
        .forEach((h) => {
          map[String(h.codhom)] = h.hom || "";
        });
      setHomMap(map);
    } catch {
      setHomMap({});
    }
  }, []);

  /* ---------- hidratar clientes visibles (por codcli) ---------- */
  const VISIBLE_TO_ENRICH = 80;
  const CONCURRENCY = 12;

  const enrichClientes = useCallback(async (list) => {
    // agarramos algunos visibles y resolvemos codcli -> nombre
    const slice = (list || []).slice(0, VISIBLE_TO_ENRICH);
    const cods = Array.from(
      new Set(
        slice
          .map((m) => Number(m?.codcli))
          .filter((n) => Number.isFinite(n) && n > 0)
      )
    );

    // Filtramos los que ya tengo en cache
    const pendientes = cods.filter((c) => !cliMap[String(c)]);
    if (pendientes.length === 0) return;

    let idx = 0;
    const localMap = { ...cliMap };

    const worker = async () => {
      while (idx < pendientes.length) {
        const cod = pendientes[idx++];
        try {
          const resp = await getClienteByCodigo(cod);
          const cli = resp?.cliente ?? resp;
          if (cli?.razonsocial) localMap[String(cod)] = cli.razonsocial;
        } catch {
          /* noop */
        }
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, pendientes.length) }, () => worker())
    );

    setCliMap(localMap);
  }, [cliMap]);

  /* ---------- fetch principal ---------- */
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const list = await getMovs(); // helper ya devuelve array
      setData(Array.isArray(list) ? list : []);
      setLoading(false);

      // catálogos / hidratación suave
      loadHoms();
      enrichClientes(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error("❌ Error getMovs:", e);
      setData([]);
      setLoading(false);
    }
  }, [loadHoms, enrichClientes]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ---------- acciones ---------- */
  const handleClose = () => {
    setShowModal(false);
    setMovEdit(null);
    fetchData();
  };

  const borrar = useCallback(async (mongoId) => {
    if (!window.confirm("¿Eliminar movimiento?")) return;
    try {
      await deleteMov(mongoId);
      setData((prev) => prev.filter((m) => m._id !== mongoId));
      fetchData();
    } catch (e) {
      console.error("❌ Error deleteMov:", e);
      alert("No se pudo borrar el movimiento.");
    }
  }, [fetchData]);

  /* ---------- filtros en memoria ---------- */
  const dataFiltrada = useMemo(() => {
    const dDesde = toStartOfDay(filtros.desde);
    const dHasta = toEndOfDay(filtros.hasta);

    const fCli = norm(filtros.cliente);
    const fHom = norm(filtros.hom);
    const fUsr = norm(filtros.usuario);

    return (data || []).filter((r) => {
      if (r?.activo === false) return false;

      const f = r?.fecmov ? new Date(r.fecmov) : null;
      if (dDesde && (!f || isNaN(f.getTime()) || f < dDesde)) return false;
      if (dHasta && (!f || isNaN(f.getTime()) || f > dHasta)) return false;

      if (fCli) {
        const cod = String(r?.codcli ?? "");
        const nombre = cliMap[String(r?.codcli)] || "";
        if (!norm(cod).includes(fCli) && !norm(nombre).includes(fCli)) return false;
      }

      if (fHom) {
        const cod = String(r?.codhom ?? "");
        const desc = homMap[String(r?.codhom)] || "";
        if (!norm(cod).includes(fHom) && !norm(desc).includes(fHom)) return false;
      }

      if (fUsr) {
        const u = String(r?.usuario1 ?? "");
        if (!norm(u).includes(fUsr)) return false;
      }

      return true;
    });
  }, [data, filtros, cliMap, homMap]);

  const limpiarFiltros = () =>
    setFiltros({ desde: "", hasta: "", cliente: "", hom: "", usuario: "" });

  const quickHoy = () => {
    const hoy = new Date();
    const iso = toISO(hoy);
    setF("desde", iso);
    setF("hasta", iso);
  };

  const quickUltimos7 = () => {
    const hoy = new Date();
    const d7 = new Date(hoy);
    d7.setDate(hoy.getDate() - 6);
    setF("desde", toISO(d7));
    setF("hasta", toISO(hoy));
  };

  /* ---------- columnas react-table ---------- */
  const columns = useMemo(() => [
    {
      id: "editar",
      Header: "Edita",
      accessor: "_id",
      width: 50,
      Cell: ({ row }) => (
        <button
          className="action-btn action-btn--edit"
          title="Editar"
          onClick={() => { setMovEdit(row.original); setShowModal(true); }}
        >
          <i className="fa fa-pencil" aria-hidden="true" />
        </button>
      ),
    },
    {
      id: "eliminar",
      Header: "🗑️",
      accessor: "_id",
      width: 50,
      Cell: ({ row }) => (
        <button
          className="action-btn action-btn--del"
          title="Eliminar"
          onClick={() => borrar(row.original._id)}
        >
          <i className="fa fa-trash-o" aria-hidden="true" />
        </button>
      ),
    },

    { Header: "Nro", accessor: "codmov", width: 70 },

    {
      Header: "Fecha",
      accessor: (row) => row?.fecmov ? new Date(row.fecmov) : null,
      id: "fecmov",
      width: 100,
      Cell: ({ value }) => (value instanceof Date && !isNaN(value.getTime()) ? fmtDateAR(value) : ""),
    },

    {
      Header: "Cliente",
      accessor: (row) => {
        const cod = row?.codcli ?? "";
        const nom = cliMap[String(cod)] || "";
        return nom ? `${cod} - ${nom}` : String(cod || "");
      },
      id: "cliente",
      width: 200,
    },

    {
      Header: "Hom",
      accessor: (row) => {
        const cod = row?.codhom ?? "";
        const nom = homMap[String(cod)] || "";
        return nom ? `${cod} - ${nom}` : String(cod || "");
      },
      id: "hom",
      width: 160,
    },

    { Header: "Usuario", accessor: "usuario1", width: 90 },

    { Header: "Leyenda", accessor: "leyenda", width: 560 },
  ], [borrar, cliMap, homMap]);

  return (
    <div className="container mt-4">
      <h3 className="text-center mb-2">LISTADO DE MOVIMIENTOS</h3>


      {/* Filtros (1 renglón, compacto) */}
      <div className="container mb-2">
        <div className="d-flex align-items-end gap-2 flex-wrap flex-lg-nowrap">

          <div style={{ minWidth: 150 }}>
            <label className="form-label mb-0 small">Desde</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={filtros.desde}
              onChange={(e) => setF("desde", e.target.value)}
            />
          </div>

          <div style={{ minWidth: 150 }}>
            <label className="form-label mb-0 small">Hasta</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={filtros.hasta}
              onChange={(e) => setF("hasta", e.target.value)}
            />
          </div>

          <div style={{ minWidth: 220 }}>
            <label className="form-label mb-0 small">Cliente</label>
            <input
              className="form-control form-control-sm"
              placeholder="cod o nombre"
              value={filtros.cliente}
              onChange={(e) => setF("cliente", e.target.value)}
            />
          </div>

          <div style={{ minWidth: 220 }}>
            <label className="form-label mb-0 small">Hom</label>
            <input
              className="form-control form-control-sm"
              placeholder="cod o descripción"
              value={filtros.hom}
              onChange={(e) => setF("hom", e.target.value)}
            />
          </div>

          <div style={{ minWidth: 120 }}>
            <label className="form-label mb-0 small">Usuario</label>
            <input
              className="form-control form-control-sm"
              placeholder="código"
              value={filtros.usuario}
              onChange={(e) => setF("usuario", e.target.value)}
            />
          </div>

          {/* Botones a la derecha */}
          <div className="ms-lg-auto d-flex gap-2">
            <button
              className="btn btn-outline-secondary btn-sm"
              style={{ minWidth: 90 }}
              onClick={quickHoy}
            >
              Hoy
            </button>

            <button
              className="btn btn-outline-primary btn-sm"
              style={{ minWidth: 110 }}
              onClick={limpiarFiltros}
            >
              Limpiar
            </button>
          </div>

        </div>
      </div>

      <Styles>
        <div className="table-container">
          <Table
            columns={columns}
            data={dataFiltrada}
            loading={loading}
            plugins={[useFilters, useSortBy, usePagination]}
          />
        </div>
      </Styles>

      {movEdit && (
        <ModalFormMov
          show={showModal}
          handleClose={handleClose}
          mov={movEdit}
        />
      )}
    </div>
  );
}